/**
 * lunopaw.js — v11 (category-aware product edition)
 * v11 changes:
 *   - injectProductEyebrow: reads product tags for pillar:* prefix,
 *     injects category-specific eyebrow instead of hardcoded string
 *   - injectCategoryBenefits: injects a category-specific .lp-benefits
 *     list above the price block (not after ATC) — Problem 4 modified
 *   - initWellnessStrip: now also category-aware
 *   - All other functions unchanged from v10
 */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var IS_PRODUCT = document.body.dataset.template
    ? document.body.dataset.template.indexOf('product') !== -1
    : window.location.pathname.indexOf('/products/') !== -1;

  /* ──────────────────────────────────────────────────────────────
   PRODUCT PILLAR SYSTEM
   Reads product tags prefixed with 'pillar:' to identify category.
   Tags set in Shopify Admin — no metafields required.
   e.g. tag 'pillar:hydration' → Hydration category
   Falls back to 'Lunopaw Wellness' if no pillar tag found.
────────────────────────────────────────────────────────────── */

  var PILLAR_MAP = {
    hydration: {
      label:    'Hydration',
      tagline:  'Supporting healthy daily drinking habits.',
      benefits: [
        'Encourages consistent water intake throughout the day',
        'Supports kidney and urinary tract health over time',
        'Reduces reliance on still-water bowls that stagnate',
      ],
      strip: ['Kidney Support', 'Daily Hydration', 'Vet-Informed', 'Premium Quality'],
    },
    comfort: {
      label:    'Comfort',
      tagline:  'Designed for deeper rest and calmer daily living.',
      benefits: [
        'Promotes quality sleep and restorative rest',
        'Reduces cortisol and stress-related behaviours',
        'Sized and shaped for natural resting postures',
      ],
      strip: ['Better Rest', 'Stress Reduction', 'Vet-Informed', 'Premium Quality'],
    },
    enrichment: {
      label:    'Enrichment',
      tagline:  'Built around what pets actually need to thrive mentally.',
      benefits: [
        'Reduces boredom and anxiety-driven destructive behaviour',
        'Stimulates natural foraging and problem-solving instincts',
        'Supports a calmer, more balanced daily routine',
      ],
      strip: ['Mental Wellness', 'Anxiety Relief', 'Vet-Informed', 'Premium Quality'],
    },
    grooming: {
      label:    'Grooming',
      tagline:  'Turning daily care into a bonding ritual.',
      benefits: [
        'Supports skin and coat health with regular use',
        'Reduces shedding and keeps coats in better condition',
        'Strengthens the owner-pet bond through handling and touch',
      ],
      strip: ['Coat Health', 'Skin Support', 'Vet-Informed', 'Premium Quality'],
    },
  };

  /* Resolve the product's pillar from its Shopify tags */
  function resolveProductPillar() {
    /* Horizon exposes product tags in a meta object on the page */
    var tags = [];
    try {
      /* Method 1: ShopifyAnalytics meta object (most reliable in Horizon) */
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta &&
          window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.tags) {
        tags = window.ShopifyAnalytics.meta.product.tags;
      }
      /* Method 2: __st context object */
      if (!tags.length && window.__st && window.__st.p) {
        tags = window.__st.p.tags || [];
      }
      /* Method 3: meta[type=application/json] (Horizon injects product JSON) */
      if (!tags.length) {
        var scriptEl = document.querySelector('script[type="application/json"][data-product-json]');
        if (scriptEl) {
          var productData = JSON.parse(scriptEl.textContent);
          tags = productData.tags || [];
        }
      }
    } catch (e) { /* silent — fall through to default */ }

    /* Find first tag matching 'pillar:*' */
    var pillarTag = tags.find ? tags.find(function (t) {
      return typeof t === 'string' && t.toLowerCase().indexOf('pillar:') === 0;
    }) : null;

    if (pillarTag) {
      var key = pillarTag.toLowerCase().replace('pillar:', '').trim();
      return PILLAR_MAP[key] || null;
    }
    return null;
  }

  /* ── 1. Homepage scroll reveal ────────────────────────────────────── */
  function initScrollReveal() {
    if (!('IntersectionObserver' in window)) return;
    var selectors = [
      '.section__heading', '.section__subheading',
      '.featured-collection .product-card', '.product-list .product-card',
      '.marquee', '.media-with-content__content', '.media-with-content__media',
      '.featured-blog-posts-card', '.collection-card', '.footer__blocks',
      '.email-signup', '[class*="ai-luxury-story"]', '[class*="ai-brand-credibility-item"]',
    ].join(', ');
    document.querySelectorAll(selectors).forEach(function (el) {
      if (el.classList.contains('lp-reveal')) return;
      el.classList.add('lp-reveal');
      var siblings = el.parentElement
        ? Array.from(el.parentElement.querySelectorAll('.product-card, .collection-card, [class*="ai-brand-credibility-item"]'))
        : [];
      var idx = siblings.indexOf(el);
      if (idx > 0 && idx <= 5) el.setAttribute('data-delay', String(idx));
    });
    if (REDUCED) {
      document.querySelectorAll('.lp-reveal').forEach(function (el) { el.classList.add('lp-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('lp-revealed'); io.unobserve(entry.target); }
      });
    }, { threshold: 0.06, rootMargin: '0px 0px -32px 0px' });
    document.querySelectorAll('.lp-reveal').forEach(function (el) { io.observe(el); });
  }

  /* ── 2. About + system-wide reveal ────────────────────────────────── */
  function initAboutReveal() {
    var els = document.querySelectorAll('.lp-ab-reveal, .lp-sys-reveal');
    if (!els.length) return;
    if (REDUCED) {
      els.forEach(function (el) {
        el.classList.add('lp-ab-visible');
        el.classList.add('lp-sys-visible');
      });
      return;
    }
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('lp-ab-visible');
        el.classList.add('lp-sys-visible');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('lp-ab-visible');
          entry.target.classList.add('lp-sys-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.07, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ── 3. Header shadow ─────────────────────────────────────────── */
  function initHeaderShadow() {
    var header = document.querySelector('header-component');
    if (!header) return;
    var ticking = false;
    function update() {
      header.style.boxShadow = window.scrollY > 60
        ? '0 2px 32px rgba(15,39,66,0.09), 0 1px 0 rgba(15,39,66,0.04)' : '';
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ── 4. Hero ──────────────────────────────────────────────────────── */
  function initHero() {
    var heroWrapper = document.querySelector('.hero-wrapper');
    if (!heroWrapper) return;
    if (!REDUCED) {
      var heroImg = heroWrapper.querySelector('.hero__media-wrapper img, .hero__media-grid img');
      if (heroImg) heroImg.classList.add('lp-kenburns');
    }
    if (!REDUCED) {
      function addHeroReady() { heroWrapper.classList.add('lp-hero-ready'); }
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { setTimeout(addHeroReady, 80); });
      } else { setTimeout(addHeroReady, 600); }
    } else {
      heroWrapper.classList.add('lp-hero-ready');
    }
    var heroSection = heroWrapper.parentElement || heroWrapper;
    if (window.getComputedStyle(heroSection).position === 'static') heroSection.style.position = 'relative';
    if (!heroSection.querySelector('.lp-hero-trust')) {
      var trustItems = [
        { number: '14,000+', label: 'Happy Families' },
        { number: '4.9 ★',   label: 'Average Rating' },
        { number: '100%',   label: 'Vet-Inspired'    },
      ];
      var strip = document.createElement('div');
      strip.className = 'lp-hero-trust';
      strip.setAttribute('aria-hidden', 'true');
      trustItems.forEach(function (item) {
        var el = document.createElement('div');
        el.className = 'lp-hero-trust-item';
        el.innerHTML = '<span class="lp-hero-trust-number">' + item.number + '</span><span class="lp-hero-trust-label">' + item.label + '</span>';
        strip.appendChild(el);
      });
      heroSection.appendChild(strip);
      strip.style.opacity = '0';
      strip.style.transition = 'opacity 600ms cubic-bezier(0.16,1,0.3,1)';
      setTimeout(function () { strip.style.opacity = '1'; }, REDUCED ? 0 : 900);
    }
    if (!REDUCED && !heroSection.querySelector('.lp-hero-scroll')) {
      var scrollEl = document.createElement('div');
      scrollEl.className = 'lp-hero-scroll';
      scrollEl.setAttribute('aria-hidden', 'true');
      var bead = document.createElement('div');
      bead.className = 'lp-hero-scroll-bead';
      scrollEl.appendChild(bead);
      heroSection.appendChild(scrollEl);
      scrollEl.style.opacity = '0';
      scrollEl.style.transition = 'opacity 400ms ease';
      setTimeout(function () {
        scrollEl.style.opacity = '1';
        setTimeout(function () { scrollEl.style.transition = 'opacity 700ms ease'; scrollEl.style.opacity = '0'; }, 2600);
      }, 2000);
    }
  }

  /* ── 5. Marquee divider ────────────────────────────────────────── */
  function initMarqueeDivider() {
    var statsSection = document.querySelector('[class*="ai-brand-credibility"]');
    if (!statsSection) return;
    var shopifySection = statsSection.closest('.shopify-section');
    if (!shopifySection || (shopifySection.nextElementSibling && shopifySection.nextElementSibling.querySelector('.lp-marquee-divider'))) return;
    var words = ['Hydration','●','Comfort','●','Enrichment','●','Grooming','●','Wellness','●','Daily Rituals','●','Happier Pets','●','Calm Lives','●'];
    var divider = document.createElement('div');
    divider.className = 'lp-marquee-divider';
    divider.setAttribute('aria-hidden', 'true');
    var track = document.createElement('div');
    track.className = 'lp-marquee-divider-track';
    var html = '';
    [words, words].forEach(function (set) {
      set.forEach(function (w) {
        html += w === '●' ? '<span class="lp-dot">●</span>' : '<span>' + w + '</span>';
      });
    });
    track.innerHTML = html;
    divider.appendChild(track);
    if (shopifySection.nextElementSibling) {
      shopifySection.parentNode.insertBefore(divider, shopifySection.nextElementSibling);
    } else {
      shopifySection.parentNode.appendChild(divider);
    }
  }

  /* ── 6. Collection card pillars ───────────────────────────────────── */
  function initCollectionCardPillars() {
    var pillars = {
      'dog-beds':    { label: 'Comfort',    desc: 'Restorative rest for every size' },
      'enrichment':  { label: 'Enrichment', desc: 'Mental wellness, daily play'     },
      'accessories': { label: 'Grooming',   desc: 'Thoughtful grooming rituals'    },
      'hydration':   { label: 'Hydration',  desc: 'Pure water, every day'           },
      'all':         { label: 'Wellness',   desc: 'Curated for a calmer life'       },
    };
    document.querySelectorAll('.collection-card, [class*="resource-card"]').forEach(function (card) {
      if (card.querySelector('.lp-pillar-label')) return;
      var link = card.querySelector('a');
      var href = link ? link.getAttribute('href') : '';
      var key = Object.keys(pillars).find(function (k) { return href && href.indexOf(k) !== -1; });
      var pillar = key ? pillars[key] : { label: 'Wellness', desc: 'Curated for a calmer life' };
      var eyebrow = document.createElement('div');
      eyebrow.className = 'lp-pillar-label';
      eyebrow.style.cssText = 'font-family:var(--lp-sans);font-size:0.58rem;letter-spacing:0.22em;text-transform:uppercase;color:var(--lp-gold);margin-bottom:0.4rem;position:relative;z-index:2;';
      eyebrow.textContent = pillar.label;
      var desc = document.createElement('p');
      desc.className = 'lp-pillar-desc';
      desc.style.cssText = 'font-family:var(--lp-sans);font-size:0.78rem;color:rgba(255,255,255,0.65);line-height:1.5;margin:0.25rem 0 0;position:relative;z-index:2;max-width:22ch;';
      desc.textContent = pillar.desc;
      var contentArea = card.querySelector('[class*="collection-card__content"], [class*="resource-card__content"]');
      if (contentArea) { contentArea.insertBefore(eyebrow, contentArea.firstChild); contentArea.appendChild(desc); }
      else {
        var wrapper = document.createElement('div');
        wrapper.style.cssText = 'position:relative;z-index:2;padding:2rem 2rem 2.5rem;margin-top:auto;width:100%;';
        wrapper.appendChild(eyebrow); wrapper.appendChild(desc); card.appendChild(wrapper);
      }
    });
  }

  /* ── 7. FAQ accordions ────────────────────────────────────────────── */
  function initFaqAccordions() {
    var faqs = document.querySelectorAll('.lp-faq__list');
    if (!faqs.length) return;
    faqs.forEach(function (list) {
      list.addEventListener('toggle', function (e) {
        if (!e.target.open) return;
        list.querySelectorAll('.lp-faq__item[open]').forEach(function (item) {
          if (item !== e.target) item.open = false;
        });
      }, true);
    });
  }

  /* ── 8. Product eyebrow — category-aware (Problem 2) ────────────────── */
  function injectProductEyebrow(pillar) {
    if (!IS_PRODUCT) return;
    var detailsPanel = document.querySelector('[class*="_product-details"]');
    if (!detailsPanel) return;
    var h1 = detailsPanel.querySelector('h1, [class*="product-title"]');
    if (!h1 || detailsPanel.querySelector('.lp-product-eyebrow')) return;

    /* Category-specific label or branded fallback */
    var eyebrowText = pillar
      ? 'Lunopaw ' + pillar.label
      : 'Lunopaw Wellness';

    var eyebrow = document.createElement('span');
    eyebrow.className = 'lp-product-eyebrow';
    eyebrow.setAttribute('aria-hidden', 'true');
    eyebrow.textContent = eyebrowText;
    h1.parentNode.insertBefore(eyebrow, h1);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { eyebrow.classList.add('lp-visible'); });
    });

    /* Category-specific tagline or branded fallback */
    if (!detailsPanel.querySelector('.lp-product-tagline')) {
      var tagline = document.createElement('span');
      tagline.className = 'lp-product-tagline';
      tagline.textContent = pillar
        ? pillar.tagline
        : 'Designed for the daily wellbeing of your pet.';
      h1.parentNode.insertBefore(tagline, h1.nextSibling);
    }
  }

  /* ── 9. Category-specific benefit list (Problem 4 modified) ─────────────
   Injected above the price block — BEFORE the purchase action,
   not after Add to Cart. Uses existing .lp-benefits CSS from
   lunopaw-system.css. No new CSS required.
  ────────────────────────────────────────────────────────────── */
  function injectCategoryBenefits(pillar) {
    if (!IS_PRODUCT) return;
    if (document.querySelector('.lp-benefits')) return;

    var detailsPanel = document.querySelector('[class*="_product-details"]');
    if (!detailsPanel) return;

    /* Find the price block to insert above it */
    var priceEl = detailsPanel.querySelector('.price, [class*="price-wrapper"], [class*="price__regular"]');
    var insertBefore = priceEl ? priceEl.closest('[class*="_product-details"] > *') || priceEl : null;
    if (!insertBefore) return;

    var benefitItems = pillar ? pillar.benefits : [
      'Vet-informed product selection',
      'Supports a specific daily wellness outcome',
      'Zero compromise on materials or safety',
    ];

    var container = document.createElement('div');
    container.style.cssText = 'margin-bottom:1.25rem;padding-bottom:1.25rem;border-bottom:1px solid var(--lp-border,#e8e4dc);';

    var heading = document.createElement('p');
    heading.className = 'lp-benefits__heading';
    heading.textContent = pillar ? 'Wellness outcomes' : 'Why Lunopaw selected this';
    container.appendChild(heading);

    var list = document.createElement('ul');
    list.className = 'lp-benefits';
    list.setAttribute('aria-label', 'Product benefits');
    benefitItems.forEach(function (text) {
      var li = document.createElement('li');
      li.className = 'lp-benefits__item';
      li.textContent = text;
      list.appendChild(li);
    });
    container.appendChild(list);

    insertBefore.parentNode.insertBefore(container, insertBefore);
  }

  /* ── 10. Wellness strip — category-aware ──────────────────────────────── */
  function initWellnessStrip(pillar) {
    if (!IS_PRODUCT) return;
    if (document.querySelector('.lp-wellness-strip')) return;
    var btnWrapper = document.querySelector('[class*="buy-buttons"], .product-form__submit, [name="add"]');
    if (!btnWrapper) return;
    var insertAfter = btnWrapper.closest('[class*="buy-buttons"]') || btnWrapper;
    var labels = pillar ? pillar.strip : ['Vet-Informed', 'Wellness-Focused', 'Eco-Friendly', 'Premium Quality'];
    var strip = document.createElement('div');
    strip.className = 'lp-wellness-strip';
    strip.setAttribute('aria-hidden', 'true');
    labels.forEach(function (label) {
      var item = document.createElement('div');
      item.className = 'lp-wellness-strip-item';
      var dot = document.createElement('span');
      dot.className = 'lp-wellness-icon';
      dot.setAttribute('aria-hidden', 'true');
      dot.style.cssText = 'display:inline-block;width:5px;height:5px;border-radius:50%;background:var(--lp-gold,#c9a96e);flex-shrink:0;';
      var lbl = document.createElement('span');
      lbl.className = 'lp-wellness-label';
      lbl.textContent = label;
      item.appendChild(dot); item.appendChild(lbl); strip.appendChild(item);
    });
    if (insertAfter.nextSibling) { insertAfter.parentNode.insertBefore(strip, insertAfter.nextSibling); }
    else { insertAfter.parentNode.appendChild(strip); }
  }

  /* ── 11. Product page micro-interactions ───────────────────────────── */
  function initProductPage() {
    if (!IS_PRODUCT) return;
    var pillar = resolveProductPillar();
    injectProductEyebrow(pillar);
    injectCategoryBenefits(pillar);
    initWellnessStrip(pillar);
    var detailsPanel = document.querySelector('[class*="_product-details"]');
    if (detailsPanel && !REDUCED) {
      Array.from(detailsPanel.children).forEach(function (el, i) {
        el.style.opacity = '0';
        el.style.transform = 'translateY(10px)';
        el.style.transition = 'opacity 0.55s cubic-bezier(0.16,1,0.3,1), transform 0.55s cubic-bezier(0.16,1,0.3,1)';
        el.style.transitionDelay = (i * 0.055) + 's';
        requestAnimationFrame(function () { requestAnimationFrame(function () { el.style.opacity = '1'; el.style.transform = 'none'; }); });
      });
    }
    if (!REDUCED) {
      var gallery = document.querySelector('[class*="product-media-gallery"], product-media-gallery');
      if (gallery) {
        var mainImg = gallery.querySelector('.media img, img');
        if (mainImg) {
          mainImg.style.transition = 'transform 0.9s cubic-bezier(0.16,1,0.3,1)';
          gallery.addEventListener('mouseenter', function () { mainImg.style.transform = 'scale(1.025)'; });
          gallery.addEventListener('mouseleave', function () { mainImg.style.transform = 'scale(1)'; });
        }
      }
    }
  }

  /* ── 12. Dynamic grid observer ──────────────────────────────────────── */
  function initDynamicObserver() {
    if (!('MutationObserver' in window)) return;
    var grid = document.querySelector('#product-grid, .main-collection');
    if (!grid) return;
    new MutationObserver(function () { setTimeout(initScrollReveal, 60); }).observe(grid, { childList: true, subtree: false });
  }

  /* ── 13. Smooth anchors ──────────────────────────────────────────── */
  function initSmoothAnchors() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;
      var target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
    });
  }

  /* ── 14. Button feedback ───────────────────────────────────────────── */
  function initButtonFeedback() {
    document.addEventListener('pointerdown', function (e) {
      var btn = e.target.closest('.button:not(.button-unstyled), .product-form__submit, [name="add"], .lp-ab-btn');
      if (!btn || REDUCED) return;
      btn.style.transform = 'scale(0.978)';
      setTimeout(function () { btn.style.transform = ''; }, 180);
    });
  }

  /* ── 15. Keyframe injection ─────────────────────────────────────────── */
  function injectKeyframes() {
    if (document.getElementById('lp-kf')) return;
    var s = document.createElement('style');
    s.id = 'lp-kf';
    s.textContent = '@keyframes lp-accord-open { from { opacity:0; transform:translateY(-5px); } to { opacity:1; transform:none; } }';
    document.head.appendChild(s);
  }

  /* ── Init ──────────────────────────────────────────────────────────────── */
  function init() {
    injectKeyframes();
    initHeaderShadow();
    initScrollReveal();
    initAboutReveal();
    initHero();
    initMarqueeDivider();
    initCollectionCardPillars();
    initFaqAccordions();
    initProductPage();
    initDynamicObserver();
    initSmoothAnchors();
    initButtonFeedback();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    initScrollReveal();
    initAboutReveal();
    initHero();
    initMarqueeDivider();
    initCollectionCardPillars();
    initFaqAccordions();
    initProductPage();
  });

})();
